/**
 * Best-effort AutoMapa WhatsApp widget analytics (M8).
 * Failures never throw — buyer flow must not block on logging.
 */
import { AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS } from "../../constants/automapAnalyticsEvents";
import { trackBuyerEvent } from "../buyerAnalytics";
import {
  AUTOMAP_WHATSAPP_INTAKE_SOURCE,
  resolveAutomapWhatsappEntryPoint,
} from "./automapWhatsappIntakeConstants";
import { hasAutomapWhatsappVehicleContext } from "./buildAutomapWhatsappVehicleContext";

export { AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS };

const SOURCE_COMPONENT = "automap_whatsapp_widget";

/**
 * @param {string} eventName
 * @param {{
 *   widgetContext?: object|null,
 *   vehicleContext?: object|null,
 *   sessionId?: string|null,
 *   leadId?: string|null,
 *   brokerRequestId?: string|null,
 *   status?: string|null,
 *   errorCode?: string|null,
 *   safeMessage?: string|null,
 *   step?: string|null,
 *   [key: string]: unknown,
 * }} [payload]
 */
export function trackAutomapWhatsappWidgetEvent(eventName, payload = {}) {
  try {
    if (!eventName) return null;

    const widgetContext = payload.widgetContext || null;
    const vehicleContext = payload.vehicleContext || null;
    const entryPoint = resolveAutomapWhatsappEntryPoint(widgetContext);
    const hasVehicle = hasAutomapWhatsappVehicleContext(vehicleContext);

    const safe = {
      source: AUTOMAP_WHATSAPP_INTAKE_SOURCE,
      clientView: "automapa",
      sourceComponent: SOURCE_COMPONENT,
      sourceAction: eventName,
      entryPoint,
      widgetSessionId: payload.sessionId || null,
      leadId: payload.leadId || null,
      brokerRequestId: payload.brokerRequestId || null,
      threadId: payload.threadId || null,
      listingId: hasVehicle
        ? vehicleContext.listingId || vehicleContext.id || null
        : payload.listingId || null,
      dealerId: hasVehicle ? vehicleContext.dealerId || null : payload.dealerId || null,
      status: payload.status || null,
      errorCode: payload.errorCode || null,
      safeMessage: payload.safeMessage || null,
      step: payload.step || null,
      financingInterest: payload.financingInterest || null,
      loggedIn: payload.loggedIn === true ? true : payload.loggedIn === false ? false : null,
      restoredVehicle: payload.restoredVehicle === true,
      analyticsDomain: "automap_whatsapp_widget",
    };

    return trackBuyerEvent(eventName, safe);
  } catch {
    return null;
  }
}

export function trackAutomapWhatsappWidgetOpened(payload = {}) {
  return trackAutomapWhatsappWidgetEvent(
    AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.WIDGET_OPENED,
    payload
  );
}

export function trackAutomapWhatsappWidgetClosed(payload = {}) {
  return trackAutomapWhatsappWidgetEvent(
    AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.WIDGET_CLOSED,
    payload
  );
}

export function trackAutomapWhatsappVehicleContextAttached(payload = {}) {
  return trackAutomapWhatsappWidgetEvent(
    AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.VEHICLE_CONTEXT_ATTACHED,
    payload
  );
}

export function trackAutomapWhatsappContinueClicked(payload = {}) {
  return trackAutomapWhatsappWidgetEvent(
    AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.CONTINUE_WHATSAPP_CLICKED,
    payload
  );
}

export function trackAutomapWhatsappDraftRestored(payload = {}) {
  return trackAutomapWhatsappWidgetEvent(
    AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.DRAFT_RESTORED,
    payload
  );
}

export function trackAutomapWhatsappDraftCleared(payload = {}) {
  return trackAutomapWhatsappWidgetEvent(
    AUTOMAP_WHATSAPP_WIDGET_ANALYTICS_EVENTS.DRAFT_CLEARED,
    payload
  );
}
